import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { autoClose, jsonError, lineCount, newline, pretty, skipClose, tab, type Edit, type JsonError } from './json-edit';

/*
 * <vitesse-json-editor> — a small JSON editor: line-number gutter, indent-aware
 * Enter and Tab, auto-closing brackets, and the parse error pointed at the line
 * it is on.
 *
 * NOT Monaco, deliberately. The one Monaco we ship (<vitesse-code-view>) is
 * read-only and registers Structured Text and XML, so JSON editing would mean
 * making a shared viewer editable AND adding a language — to load a 3.7 MB chunk
 * into a modal that edits a single value. What that modal actually needs is
 * formatting, an error you can find, and typing that does not fight you, which
 * is a textarea and the rules in json-edit.ts.
 *
 * Every rule lives in json-edit.ts as a pure function; this file is the DOM half
 * — the gutter, the key bindings, and keeping the two scrolled together.
 *
 * `change` fires per keystroke with { value }, matching <vitesse-text-input>.
 */
@customElement('vitesse-json-editor')
export class VitesseJsonEditor extends LitElement {
  @property() value = '';
  @property({ type: Number }) rows = 12;
  @property({ type: Boolean }) disabled = false;

  /** Scroll offset of the textarea, mirrored onto the gutter.
   *  NOT named `scrollTop`: HTMLElement already defines that as a public
   *  property, and shadowing it with a private field is a type error — and would
   *  have meant the element lying about its own scroll position. */
  @state() private gutterOffset = 0;

  @query('textarea') private area?: HTMLTextAreaElement;

  static styles = css`
    :host {
      display: block;
    }
    .frame {
      display: flex;
      border: 1px solid var(--border-color);
      border-radius: 6px;
      background: var(--bg-secondary, var(--bg-primary));
      overflow: hidden;
      font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
      font-size: 12.5px;
      line-height: 1.5;
    }
    .frame:focus-within {
      border-color: var(--blue);
    }
    .frame.bad:focus-within,
    .frame.bad {
      border-color: color-mix(in srgb, var(--red) 55%, var(--border-color));
    }
    /* The gutter scrolls with the text but is not itself scrollable — it is a
       readout, never a target, so it takes no pointer events and no selection. */
    .gutter {
      flex: none;
      padding: 8px 8px 8px 10px;
      text-align: right;
      color: var(--text-tertiary, var(--text-secondary));
      background: color-mix(in srgb, var(--text-primary) 4%, transparent);
      border-right: 1px solid var(--border-color);
      user-select: none;
      pointer-events: none;
      overflow: hidden;
    }
    .gutter div {
      opacity: 0.65;
    }
    /* The line the error is on, so "line 3" is findable without counting. */
    .gutter div.bad {
      opacity: 1;
      color: var(--red);
      font-weight: 600;
    }
    textarea {
      flex: 1;
      min-width: 0;
      padding: 8px 10px;
      border: 0;
      outline: none;
      resize: vertical;
      background: transparent;
      color: var(--text-primary);
      font: inherit;
      tab-size: 2;
      white-space: pre;
      overflow-wrap: normal;
      overflow-x: auto;
    }
    textarea:disabled {
      opacity: 0.6;
    }
  `;

  /** The current parse failure, or null. Recomputed on render — the text is a
   *  value in a dialog, not a document, so this is never hot. */
  get error(): JsonError | null {
    return jsonError(this.value);
  }

  /** Replace the text and restore the caret, then tell the host. Selection must
   *  be set AFTER Lit has written the new value, or the browser puts the caret
   *  at the end and every bracket typed mid-document jumps to the bottom. */
  private applyEdit(e: Edit): void {
    this.value = e.text;
    this.emit();
    void this.updateComplete.then(() => {
      const ta = this.area;
      if (ta) ta.setSelectionRange(e.start, e.end);
    });
  }

  private emit(): void {
    this.dispatchEvent(new CustomEvent('change', { detail: { value: this.value }, bubbles: true, composed: true }));
  }

  /** Reformat, keeping the caret at the start of the line it was on — an
   *  absolute offset is meaningless once every line's indentation has changed. */
  format(): void {
    const next = pretty(this.value);
    if (next === this.value) return;
    const ta = this.area;
    const line = ta ? this.value.slice(0, ta.selectionStart).split('\n').length : 1;
    this.value = next;
    this.emit();
    void this.updateComplete.then(() => {
      const el = this.area;
      if (!el) return;
      const lines = next.split('\n');
      const offset = lines.slice(0, Math.min(line, lines.length) - 1).join('\n').length + (line > 1 ? 1 : 0);
      el.setSelectionRange(offset, offset);
      el.focus();
    });
  }

  private onKeyDown(ev: KeyboardEvent): void {
    const ta = ev.target as HTMLTextAreaElement;
    const { selectionStart: s, selectionEnd: e } = ta;

    // Cmd/Ctrl+Shift+F — the format shortcut every editor has.
    if ((ev.metaKey || ev.ctrlKey) && ev.shiftKey && ev.key.toLowerCase() === 'f') {
      ev.preventDefault();
      this.format();
      return;
    }
    // Let the dialog's own shortcuts through untouched.
    if (ev.metaKey || ev.ctrlKey || ev.altKey) return;

    if (ev.key === 'Tab') {
      ev.preventDefault();
      this.applyEdit(tab(this.value, s, e, ev.shiftKey));
      return;
    }
    if (ev.key === 'Enter') {
      ev.preventDefault();
      this.applyEdit(newline(this.value, s, e));
      return;
    }
    const opened = autoClose(ev.key, this.value, s, e);
    if (opened) {
      ev.preventDefault();
      this.applyEdit(opened);
      return;
    }
    const skipped = skipClose(ev.key, this.value, s, e);
    if (skipped) {
      ev.preventDefault();
      this.applyEdit(skipped);
    }
  }

  private onInput(ev: Event): void {
    this.value = (ev.target as HTMLTextAreaElement).value;
    this.emit();
  }

  render() {
    const err = this.error;
    const n = lineCount(this.value);
    const lines = [];
    for (let i = 1; i <= n; i++) {
      lines.push(html`<div class=${err && err.line === i ? 'bad' : ''}>${i}</div>`);
    }
    return html`
      <div class="frame ${err ? 'bad' : ''}">
        <div class="gutter" style="transform: translateY(${-this.gutterOffset}px)">${lines}</div>
        <textarea
          rows=${this.rows}
          spellcheck="false"
          autocapitalize="off"
          autocorrect="off"
          ?disabled=${this.disabled}
          .value=${this.value}
          @input=${this.onInput}
          @keydown=${this.onKeyDown}
          @scroll=${(ev: Event) => (this.gutterOffset = (ev.target as HTMLTextAreaElement).scrollTop)}
        ></textarea>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-json-editor': VitesseJsonEditor;
  }
}
